// Modele de donnees de la vue acheteur operation-bci (V2 — paniers importes).
// Cloné de SuiviOperationBCV2/types.ts. Differences BCI :
//   - UNE seule commande par operation (pas de cascade, pas de multi-prestataires) ;
//     `commande.rang` peut valoir 1 ou 2, mais il n'y a qu'une commande liee a l'operation.
//   - Onglets reduits : Consultation + Offre uniquement (ni Messagerie, ni Evaluation, ni Historique).
//   - Pas de signature/OTP cote BCI.
// Source unique des codes de statut : table `status` (partagee BC/BCI).

export enum StatusId {
  BrouillonAcheteur = 1,
  BrouillonPrestataire = 2,
  AttenteAcceptationRang1 = 3,
  AAccepter = 4,
  AttenteAcceptationRang2 = 5,
  AttenteAcceptationRang3 = 6,
  Valide = 7, // Accepte / Valide
  Refuse = 8,
  Expire = 9,
  AttenteOffre = 10,
  AChiffrer = 11,
  AAnalyser = 12,
  AttenteAnalyse = 13,
  Annule = 14,
  PropositionFaite = 15, // En attente de decision de l'acheteur
}

// Le rang est porte par l'unique commande (souvent 1, parfois 2). Pas de cascade.
export type Rang = 1 | 2 | 3;

// BCI : seulement deux onglets cote acheteur.
export type TabKind = 'consultation' | 'offre';

// Un onglet est une donnee, pas un libelle a re-parser ni un index a deviner.
export interface TabDescriptor {
  key: string;
  kind: TabKind;
  rang?: Rang;
  label: string;
  disabled: boolean;
  inactive: boolean;
}

export interface StatusRef {
  id?: number;
  nom?: string;
}

// Article du catalogue tel que renvoye par l'API (porte par la commande).
export interface CatalogueArticleData {
  id?: number | string;
  quantity?: number | string;
  quantite?: number | string;
  prix_unitaire?: number | string;
  prix?: number | string;
  prix_total?: number | string;
  totalHT?: number | string;
}

export interface CatalogueItem {
  id?: number | string;
  code?: number | string;
  nom?: string;
  description?: string;
  unite?: string;
  seuil_a?: number;
  seuil_b?: number;
  article?: CatalogueArticleData;
}

export interface CommandeArticle {
  catalogue?: CatalogueItem;
}

// Evenement d'historique (commande ou operation).
export interface HistoriqueEvent {
  prefix?: string;
  type?: string;
  label?: string | null;
  message?: string;
  type_compte?: string;
  created_at?: string;
  updated_at?: string;
}

export interface EntiteRef {
  nom?: string;
}

export interface ContratRef {
  entite?: EntiteRef;
}

export interface LotTechnique {
  id?: number | string;
  nom?: string;
}

export interface LotDepartement {
  departement?: { id?: number | string; nom?: string };
}

export interface OrganismeRef {
  id?: number;
  nom?: string;
}

// Acheteur signataire / validateur (cible d'une demande de validation).
export interface Signataire {
  id: number;
  type: string;
  prenom?: string;
  nom?: string;
  fonction?: string;
  email_contact?: string;
}

// Sac de formulaire (cote front) : champs connus + reste libre (saisie utilisateur).
export interface OperationFormulaire {
  referenceInterne?: string;
  nomOperation?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  coordonneesMaitrise?: string;
  coordonneesMaitriseDOuvrage?: string;
  delaisConsultation?: number | string;
  delaisFinOperation?: number | string;
  visitePreavis?: boolean | number | string;
  conditionVisite?: string;
  dateDebutConsultation?: string;
  dateFinConsultation?: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
  besoin?: string;
  [key: string]: unknown;
}

// L'unique commande liee a l'operation BCI (rang 1 ou 2).
export interface CommandeRang {
  id?: number;
  rang: string | number;
  id_status: number;
  articles?: CommandeArticle[];
  historique?: HistoriqueEvent[];
  contrat?: ContratRef;
  type_compte?: string;
  updated_at?: string;
  [key: string]: unknown;
}

// Entite operation-bci telle que consommee par la vue acheteur. Champs declares
// explicitement ; l'index signature reste un filet `unknown` (jamais `any`).
export interface OperationBCI {
  id: number;
  numero?: string | number;
  nom?: string;
  reference?: string;
  besoin?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  facturation?: string;
  coordonnees_maitrise_ouvrage?: string;
  coordonnees_maitrise?: string;
  consultation?: number | string;
  delai_execution?: number | string;
  delais?: number | string;
  visite?: boolean | number | string;
  condition_visite?: string;
  date_debut_operation?: string;
  date_fin_operation?: string;
  id_status?: number;
  status?: StatusRef;
  rang_actuel?: number;
  // Une seule commande liee a l'operation (BCI). Tableau conserve pour coller a l'API.
  commandes?: CommandeRang[];
  historique?: HistoriqueEvent[];
  lot_technique?: LotTechnique;
  id_lot_departement?: number;
  lot_departement?: LotDepartement;
  organisme?: OrganismeRef;
  acheteur?: { nom?: string };
  prestataire?: { nom?: string };
  operation?: OperationBCI; // certaines reponses imbriquent l'operation
  expire_at?: string;
  organisme_nom?: string;
  created_at?: string;
  updated_at?: string;
  formulaire?: OperationFormulaire;
  [key: string]: unknown;
}
